export const AGENDAPRO_NAME = 'AgendaPro';

// Countries where agendapro.com publishes its own client privacy policy; any other one uses the Spain page.
const AGENDAPRO_PRIVACY_COUNTRIES = ['cl', 'ar', 'mx', 'co', 'pe', 'es', 'br', 'us'];
const AGENDAPRO_PRIVACY_FALLBACK_COUNTRY = 'es';

const UNSAFE_SCHEME = /^(javascript|data|vbscript|file):/i;
const HTTP_SCHEME = /^https?:/i;
// A "scheme:" not followed by a port, so "host:8080/path" still counts as having no scheme.
const OTHER_SCHEME = /^[a-z][a-z\d+.-]*:(?!\d+(\/|$))/i;

export const getAgendaProPrivacyPolicyUrl = (countryCode?: string | null): string => {
  const country = countryCode?.trim().toLowerCase();
  const policyCountry =
    country && AGENDAPRO_PRIVACY_COUNTRIES.includes(country) ? country : AGENDAPRO_PRIVACY_FALLBACK_COUNTRY;

  return `https://agendapro.com/${policyCountry}/politica-de-privacidad-clientes`;
};

// policy_term is free text typed by the company, so it only becomes a link when it is a real http(s) URL.
export const sanitizePolicyUrl = (raw?: string | null): string | null => {
  const trimmed = raw?.trim();

  if (!trimmed || UNSAFE_SCHEME.test(trimmed)) {
    return null;
  }

  const hasHttpScheme = HTTP_SCHEME.test(trimmed);

  if (!hasHttpScheme && OTHER_SCHEME.test(trimmed)) {
    return null;
  }

  try {
    const url = new URL(hasHttpScheme ? trimmed : `https://${trimmed}`);

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }

    // Free text still parses in some browsers (Chrome turns "SON APROXIMADOS." into a %20-encoded host with a
    // trailing dot), so only a real domain counts. The last label must start with a letter: "10.000" would
    // otherwise parse as the IP 10.0.0.0.
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z][a-z0-9-]*$/.test(url.hostname)) {
      return null;
    }

    return url.toString();
  } catch {
    return null;
  }
};

type BookingPrivacyPolicyParams = {
  policyTermActive?: boolean | null;
  policyTerm?: string | null;
  companyName?: string | null;
  countryCode?: string | null;
};

export type BookingPrivacyPolicy = {
  url: string;
  owner: string;
};

export const resolveBookingPrivacyPolicy = ({
  policyTermActive,
  policyTerm,
  companyName,
  countryCode,
}: BookingPrivacyPolicyParams): BookingPrivacyPolicy => {
  const companyPolicyUrl = policyTermActive === true ? sanitizePolicyUrl(policyTerm) : null;
  const owner = companyName?.trim();

  // Without a name to cite the sentence would end in "de .", so it falls back to AgendaPro too.
  if (companyPolicyUrl && owner) {
    return { url: companyPolicyUrl, owner };
  }

  return { url: getAgendaProPrivacyPolicyUrl(countryCode), owner: AGENDAPRO_NAME };
};
